import { Grid } from '@farewill/ui'
import { useFormikContext } from 'formik'

import Input from 'components/form/input'
import { YES_NO_OPTIONS } from 'utils/enums'
import { strToBool, strToBoolProp } from 'utils/helpers'

import withForm from 'lib/formik/with-form'
import useValidatedSave from 'lib/formik/use-validated-save'
import { WILL_OR_INTESTACY_SCHEMA } from 'lib/probate/estate-questionnaire-schema'
import { ProbateEstate } from 'lib/models/probate-estate'

import Section from '../components/section'
import RadioGroup from '../components/radio-group'

type HandleSave = (event: { name: string; value: unknown }) => void

type Props = {
  probateEstate: ProbateEstate
  saveProbateEstateField: HandleSave
  onHasWillChange: (hasWill: boolean | undefined) => void
}

const WillOrIntestacyFields = ({
  saveProbateEstateField,
  onHasWillChange,
}: Pick<Props, 'saveProbateEstateField' | 'onHasWillChange'>) => {
  const { values } = useFormikContext<Record<string, unknown>>()

  const handleSaveEstateField = useValidatedSave({
    schema: WILL_OR_INTESTACY_SCHEMA,
    save: saveProbateEstateField,
  })

  const handleHasWillChange = ({
    name,
    value,
  }: {
    name: string
    value: string
  }) => {
    handleSaveEstateField({ name, value })
    onHasWillChange(strToBool(value as strToBoolProp))
  }

  return (
    <Section
      heading="1.2 Will or intestacy"
      notesName="willOrIntestacyNotes"
      onNotesSave={handleSaveEstateField}
    >
      <Grid.Item span={12}>
        <RadioGroup
          label="Is there a will?"
          name="hasWill"
          options={YES_NO_OPTIONS}
          handleSave={handleHasWillChange}
          inline
        />
      </Grid.Item>
      {strToBool(values.hasWill as strToBoolProp) !== false && (
        <Grid.Item span={12}>
          <Input
            name="willLocation"
            label="Where is the will?"
            component="textarea"
            handleSave={handleSaveEstateField}
          />
        </Grid.Item>
      )}
    </Section>
  )
}

const WillOrIntestacyForm = withForm(WillOrIntestacyFields)

const WillOrIntestacy = ({
  probateEstate,
  saveProbateEstateField,
  onHasWillChange,
}: Props) => {
  const attrs = probateEstate.attributes

  const initialValues = {
    hasWill: attrs.hasWill ?? null,
    willLocation: attrs.willLocation ?? null,
    willOrIntestacyNotes: attrs.willOrIntestacyNotes ?? null,
  }

  return (
    <WillOrIntestacyForm
      initialValues={initialValues}
      validationSchema={WILL_OR_INTESTACY_SCHEMA}
      saveProbateEstateField={saveProbateEstateField}
      onHasWillChange={onHasWillChange}
    />
  )
}

export default WillOrIntestacy
